/**
 * streamAssistantChat — SSE consumer for `POST /assistant/chat`.
 *
 * The BE chat endpoint streams progress as Server-Sent Events; every
 * frame is `data: {json}` and the JSON object carries an `event`
 * discriminator.  Wire shape verified against
 * `backend/app/services/assistant/agent_loop.py` +
 * `backend/app/api/assistant.py:699-783` on `origin/dev`.
 *
 * Reuses `parseSseStream` (the same helper `streamScan` uses) so the
 * frame parser is the proven one.
 *
 * Notable contract clarifications surfaced during the FE audit
 * (see frontend#259 + frontend#261 comments):
 *  - `tool_result` carries `call_id` + `result` ONLY (no `tool`
 *    field). Correlate to `tool_call` via `call_id`.
 *  - Cross-tenant errors return HTTP 404, not 403 — branch on the
 *    response body's `code` field, not the HTTP status. (Not
 *    relevant to this file — it's relevant for the thread-drawer
 *    consumer in #261.)
 *  - Stream always ends with exactly one `done` or `error` event.
 *  - On mid-stream disconnect, BE tears down cleanly — the user
 *    message was persisted BEFORE the stream began.
 *
 * Usage:
 *   for await (const ev of streamAssistantChat({user_message, thread_id})) {
 *     switch (ev.event) {
 *       case 'thread': ...
 *       case 'content': ...
 *       case 'done': return;
 *     }
 *   }
 *
 * Errors:
 *   - Pre-stream 4xx/5xx → throws `AssistantHttpError` (consumer
 *     reads `err.status` + `err.code` to surface a friendly toast).
 *   - Mid-stream terminal failure → yielded as an `error` event with
 *     `{message, code, retryable}`.
 *   - Abort via `opts.signal` → reader closes; finally{} fires.
 */

import { Platform } from 'react-native';
import { parseSseStream } from '../../../api/sseParser';

const API_BASE_URL =
  process.env.EXPO_PUBLIC_API_BASE_URL || 'https://backend.remembr.xyz';
const AUTH_TOKEN_STORAGE_KEY = 'auth_token';

// ─── Wire types ─────────────────────────────────────────────────────

export interface ChatAttachment {
  type: 'image';
  /** FileRecord id obtained from the existing upload path. */
  file_id: string;
}

export interface AssistantChatRequest {
  /** User's prompt — required. */
  user_message: string;
  /** Optional ULID; omit/null → BE creates a new thread (first event
   *  is `thread` with the id). */
  thread_id?: string | null;
  /** Optional attachments — only `type: 'image'` supported. See #264. */
  attachments?: ChatAttachment[];
}

// One discriminated union per emitted event type.  Fields match
// `backend/app/services/assistant/agent_loop.py` exactly.

export type AssistantSseEvent =
  | { event: 'thread'; thread_id: string; title: string }
  | { event: 'thinking' }
  | {
      event: 'tool_call';
      tool: string;
      args: Record<string, unknown>;
      call_id: string;
    }
  | {
      event: 'tool_result';
      // NOTE: no `tool` field on this event — correlate to the
      // earlier `tool_call` via `call_id`.
      call_id: string;
      result: {
        status: string;
        data: unknown;
        error_code: string | null;
      };
    }
  | { event: 'content'; token: string }
  | {
      event: 'done';
      /** The authoritative full answer text.  Equal to the
       *  concatenation of all prior `content` tokens — but use this
       *  field as the source of truth (the BE guarantees it). */
      content: string;
      latency_ms: number;
      tokens_in: number;
      tokens_out: number;
      cost_usd: number;
      iterations: number;
      model: string;
      provider: string;
    }
  | {
      event: 'error';
      message: string;
      /** e.g. `founsi_llm_provider_error`,
       *  `founsi_max_tool_iterations`. */
      code: string;
      retryable: boolean;
    };

// ─── Errors ────────────────────────────────────────────────────────

export class AssistantHttpError extends Error {
  status: number;
  code: string | null;
  context: Record<string, unknown> | null;
  constructor(
    status: number,
    detail: string,
    code: string | null,
    context: Record<string, unknown> | null,
  ) {
    super(detail);
    this.name = 'AssistantHttpError';
    this.status = status;
    this.code = code;
    this.context = context;
  }
}

// ─── Helpers ───────────────────────────────────────────────────────

const getWebAuthToken = (): string | null => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  return window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
};

// ─── Consumer ──────────────────────────────────────────────────────

export interface StreamChatOptions {
  signal?: AbortSignal;
}

export async function* streamAssistantChat(
  body: AssistantChatRequest,
  opts: StreamChatOptions = {},
): AsyncGenerator<AssistantSseEvent, void, void> {
  if (Platform.OS !== 'web') {
    // Same constraint as streamScan — RN 0.72 fetch doesn't expose
    // Response.body as a ReadableStream.  A native-side path (e.g.
    // react-native-sse) is a follow-up; the chat tab ships web-first.
    throw new Error('Assistant chat streaming is web-only in this build');
  }

  const token = getWebAuthToken();

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/assistant/chat`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({
        user_message: body.user_message,
        thread_id: body.thread_id ?? undefined,
        attachments: body.attachments ?? undefined,
      }),
      signal: opts.signal,
    });
  } catch (err) {
    // Pre-stream network failure — let the consumer's outer catch
    // surface a "couldn't reach Founsi" toast.
    throw err;
  }

  if (!res.ok) {
    // BE structured-error shape: { detail, code, context }.  Parse
    // and surface so the FE can branch on `code` (e.g.
    // `founsi_rate_limit_exceeded`) for a friendly message.
    let detail = res.statusText || 'Request failed';
    let code: string | null = null;
    let context: Record<string, unknown> | null = null;
    try {
      const errBody = await res.json();
      if (errBody?.detail) {
        detail = Array.isArray(errBody.detail)
          ? errBody.detail
              .map((d: any) => d?.msg || JSON.stringify(d))
              .join(', ')
          : String(errBody.detail);
      }
      if (typeof errBody?.code === 'string') code = errBody.code;
      if (errBody?.context && typeof errBody.context === 'object') {
        context = errBody.context;
      }
    } catch {
      /* keep statusText fallback */
    }
    throw new AssistantHttpError(res.status, detail, code, context);
  }

  if (!res.body) {
    // Defensive — modern browsers all expose .body, but Safari
    // sometimes ships behind a flag.  Bail loudly rather than spin.
    throw new Error(
      'Assistant chat response had no body (browser support gap).',
    );
  }

  const textStream = res.body.pipeThrough(new TextDecoderStream());
  for await (const frame of parseSseStream<AssistantSseEvent>(textStream)) {
    // `frame.data` is already JSON-parsed by parseSseStream.  Just
    // yield it — the discriminator lives on `data.event`.
    if (frame?.data && typeof (frame.data as any).event === 'string') {
      yield frame.data as AssistantSseEvent;
    }
  }
}
